import { getSortedTowLog, type ITowLogEvent } from '../../tow-log';
import type {
  ICprMathContext,
  IProcessedTowLogSection,
} from '../interface/cpr-math-context.interface';
import { getDivisionsPerBlock } from './divisions-per-block';
import { calculateTowedMiles, getTowedMiles } from './towed-miles';

/**
 * Breaks the tow log into sections and calculates many things.
 *
 * Each section of a tow log is a period of the journey that the tow was in the water. It begins
 * with a `SHOOT` or `MIDSHOOT` and ends with a `HAUL` or `MIDHAUL`.
 *
 * @param towLog The complete tow log. It can be unordered.
 */
export function getProcessedTowLogSections<ObjectId>(
  context: ICprMathContext<ObjectId>,
): IProcessedTowLogSection<ObjectId>[] {
  if (context.sections) {
    return context.sections;
  }

  const { tow, towLog } = context;

  if (!tow) {
    throw TypeError('Missing tow');
  }

  if (!towLog?.length) {
    throw TypeError('Missing tow log');
  }

  if (typeof tow.silkReadingStart !== 'number' || typeof tow.silkReadingEnd !== 'number') {
    throw TypeError('Tow is missing silk reading start and/or end');
  }

  let silkStart = tow.silkReadingStart;

  const silkLength = tow.silkReadingEnd - tow.silkReadingStart;

  const orderedEvents = getSortedTowLog(towLog);

  const towLogTowedMiles = getTowedMiles({ tow, towLog: orderedEvents })!;

  return (context.sections = orderedEvents
    .reduce(
      (sections, towLogEvent) => {
        // SHOOT starts a new section
        if (towLogEvent.eventCode === 'SHOOT' || towLogEvent.eventCode === 'MIDSHOOT') {
          sections.push([]);
        }

        const currentSection = sections.at(-1);

        const previousEventCode = currentSection?.at(-1)?.eventCode;

        // HAUL ends a section - do not add additional events
        if (previousEventCode !== 'HAUL' && previousEventCode !== 'MIDHAUL') {
          currentSection?.push(towLogEvent);
        }

        return sections;
      },
      [] as ITowLogEvent<ObjectId, Date>[][],
    )
    .map((events): IProcessedTowLogSection<ObjectId> => {
      const startTime = events[0]!.eventDate;

      const endTime = events.at(-1)!.eventDate;

      const milesTowed = calculateTowedMiles(events);

      const silkEnd = silkStart + (milesTowed / towLogTowedMiles) * silkLength;

      const divisionsRun = silkEnd - silkStart;

      const milesPerDivision = milesTowed / divisionsRun;

      const divisionsPerBlock = getDivisionsPerBlock(milesPerDivision);

      const averageSpeed = milesTowed / ((endTime.getTime() - startTime.getTime()) / 36e5);

      const milesSampled = milesPerDivision ? milesTowed - milesPerDivision : 0;

      let factor!: number;

      // Calculate factor - lifted directly from old FORTRAN program
      if (divisionsPerBlock >= 3.2) {
        factor = 1.6;
      } else if (divisionsPerBlock >= 3.0) {
        factor = 1.5;
      } else if (divisionsPerBlock >= 2.8) {
        factor = 1.4;
      } else if (divisionsPerBlock >= 2.6) {
        factor = 1.3;
      } else if (divisionsPerBlock >= 2.4) {
        factor = 1.2;
      } else if (divisionsPerBlock >= 2.2) {
        factor = 1.1;
      } else if (divisionsPerBlock >= 1.8) {
        factor = 1.0;
      } else if (divisionsPerBlock >= 1.6) {
        factor = 0.9;
      } else if (divisionsPerBlock >= 1.4) {
        factor = 0.8;
      } else if (divisionsPerBlock >= 1.2) {
        factor = 0.7;
      } else if (divisionsPerBlock >= 1.0) {
        factor = 0.6;
      } else if (divisionsPerBlock === 0) {
        factor = 0;
      } else {
        factor = 0.5;
      }

      const returnedSilkStart = silkStart;
      silkStart = silkEnd;

      return {
        events,
        startTime,
        endTime,
        milesTowed,
        silkStart: returnedSilkStart,
        silkEnd,
        divisionsRun,
        milesPerDivision,
        divisionsPerBlock,
        averageSpeed,
        factor,
        milesSampled,
      };
    }));
}
